import React from "react";
import Cropper from "react-easy-crop";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { X as XIcon, ZoomIn, ZoomOut, RotateCw, Upload, Check } from "lucide-react";

/**
 * Per-platform target sizes (pixels) + aspect ratios. These are the
 * recommended 2026 specs — each tuple is (label, aspect, width, height).
 * The first entry in each platform's list is the default preset.
 * `typicalKb` is an order-of-magnitude estimate of what the cropper
 * outputs (PNG for illustrations, JPEG fallback via auto-compression).
 */
export const PLATFORM_SPECS = {
  bluesky:   { maxKb: 1000, formatNote: "PNG or JPEG · auto-compresses to JPEG if > 1 MB" },
  facebook:  { maxKb: 8000, formatNote: "PNG or JPEG · ≤ 8 MB" },
  instagram: { maxKb: 8000, formatNote: "PNG or JPEG · ≤ 8 MB" },
  twitter:   { maxKb: 5000, formatNote: "PNG or JPEG · ≤ 5 MB" },
  tiktok:    { maxKb: 20000, formatNote: "PNG or JPEG · ≤ 20 MB" },
  substack:  { maxKb: 10000, formatNote: "PNG or JPEG · Substack displays at ~1456×816 px · ≤ 10 MB" },
};

export const PLATFORM_PRESETS = {
  bluesky: [
    { id: "square",    label: "Square 1:1",    aspect: 1,      w: 1080, h: 1080, typicalKb: "250–600" },
    { id: "portrait",  label: "Portrait 4:5",  aspect: 4 / 5,  w: 1080, h: 1350, typicalKb: "300–700" },
    { id: "landscape", label: "Landscape 16:9", aspect: 16 / 9, w: 1600, h: 900,  typicalKb: "300–700" },
  ],
  facebook: [
    { id: "feed",      label: "Feed 1.91:1",   aspect: 1.91,   w: 1200, h: 630,  typicalKb: "200–500" },
    { id: "square",    label: "Square 1:1",    aspect: 1,      w: 1200, h: 1200, typicalKb: "300–700" },
    { id: "portrait",  label: "Portrait 4:5",  aspect: 4 / 5,  w: 1080, h: 1350, typicalKb: "300–700" },
  ],
  instagram: [
    { id: "square",    label: "Square 1:1",    aspect: 1,      w: 1080, h: 1080, typicalKb: "250–600" },
    { id: "portrait",  label: "Portrait 4:5",  aspect: 4 / 5,  w: 1080, h: 1350, typicalKb: "300–700" },
    { id: "landscape", label: "Landscape 1.91:1", aspect: 1.91, w: 1080, h: 566,  typicalKb: "150–400" },
  ],
  twitter: [
    { id: "landscape", label: "Landscape 16:9", aspect: 16 / 9, w: 1600, h: 900,  typicalKb: "300–700" },
    { id: "square",    label: "Square 1:1",    aspect: 1,      w: 1200, h: 1200, typicalKb: "300–700" },
  ],
  tiktok: [
    { id: "portrait",  label: "Portrait 9:16", aspect: 9 / 16, w: 1080, h: 1920, typicalKb: "400–900" },
  ],
  // Substack hero image — 1456×816 is the exact size Substack renders at.
  // Also offering square + wider banner variants that Substack accepts.
  substack: [
    { id: "hero",      label: "Hero 16:9 (recommended)", aspect: 1456 / 816, w: 1456, h: 816, typicalKb: "300–800" },
    { id: "wide",      label: "Wide 3:1 banner",         aspect: 3,          w: 1500, h: 500, typicalKb: "250–600" },
    { id: "square",    label: "Square 1:1",              aspect: 1,          w: 1200, h: 1200, typicalKb: "400–900" },
  ],
};

const MAX_IMAGES = { bluesky: 4, facebook: 4, instagram: 4, twitter: 4, tiktok: 1, substack: 1 };

export function platformMaxImages(platform) {
  return MAX_IMAGES[platform] ?? 4;
}

function formatKb(kb) {
  if (kb >= 1000) return `${(kb / 1000).toFixed(0)} MB`;
  return `${kb} KB`;
}

/**
 * Produce a cropped image as a PNG Blob at the preset's target pixel size.
 * Runs entirely client-side via a canvas — the cropped output is what we
 * upload, not the original (so uploads stay small and on-aspect).
 */
async function getCroppedBlob(imageSrc, pixelCrop, rotation, targetW, targetH) {
  const image = await new Promise((resolve, reject) => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = imageSrc;
  });

  // First: render the rotated source to an offscreen canvas the size of the
  // source image's bounding box. Then crop out pixelCrop from *that* canvas.
  const rad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rad)), cos = Math.abs(Math.cos(rad));
  const bboxW = image.width * cos + image.height * sin;
  const bboxH = image.width * sin + image.height * cos;

  const rotCanvas = document.createElement("canvas");
  rotCanvas.width = bboxW;
  rotCanvas.height = bboxH;
  const rctx = rotCanvas.getContext("2d");
  rctx.translate(bboxW / 2, bboxH / 2);
  rctx.rotate(rad);
  rctx.drawImage(image, -image.width / 2, -image.height / 2);

  // Crop
  const outCanvas = document.createElement("canvas");
  outCanvas.width = targetW;
  outCanvas.height = targetH;
  const octx = outCanvas.getContext("2d");
  octx.imageSmoothingQuality = "high";
  octx.drawImage(
    rotCanvas,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, targetW, targetH,
  );

  return await new Promise((resolve) =>
    outCanvas.toBlob((blob) => resolve(blob), "image/png", 0.95),
  );
}

/**
 * ImageCropModal — drop target + cropper + alt-text field. On save, uploads
 * the cropped blob to /api/images/upload and calls onSave({url, alt, preset}).
 */
export function ImageCropModal({ open, platform, onClose, onSave, initialFile = null }) {
  const presets = PLATFORM_PRESETS[platform] || PLATFORM_PRESETS.bluesky;
  const [file, setFile] = React.useState(initialFile);
  const [imageSrc, setImageSrc] = React.useState(null);
  const [presetIdx, setPresetIdx] = React.useState(0);
  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);
  const [rotation, setRotation] = React.useState(0);
  const [pixelCrop, setPixelCrop] = React.useState(null);
  const [alt, setAlt] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [dragActive, setDragActive] = React.useState(false);

  const preset = presets[presetIdx];
  const spec = PLATFORM_SPECS[platform] || PLATFORM_SPECS.bluesky;

  React.useEffect(() => {
    if (!open) return;
    setFile(initialFile);
    setImageSrc(null);
    setPresetIdx(0);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setAlt("");
    setPixelCrop(null);
  }, [open, initialFile]);

  React.useEffect(() => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result);
    reader.readAsDataURL(file);
  }, [file]);

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) {
      toast.error("Image is over 20 MB. Please use something smaller.");
      return;
    }
    if (!f.type.startsWith("image/")) {
      toast.error("That doesn't look like an image file.");
      return;
    }
    setFile(f);
  };

  // Drag-and-drop handlers. Crucially, preventDefault on dragover is what
  // stops the browser's default "open the dropped file as a URL in this tab"
  // behaviour — without it, dropping an image onto the page navigates away.
  const onDragOver = React.useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    setDragActive(true);
  }, []);
  const onDragLeave = React.useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only flip off when leaving the modal itself, not child elements.
    if (e.currentTarget === e.target) setDragActive(false);
  }, []);
  const onDrop = React.useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) handleFile(files[0]);
  }, []);

  const onCropComplete = React.useCallback((_a, pc) => setPixelCrop(pc), []);

  const save = async () => {
    if (!imageSrc || !pixelCrop) {
      toast.error("Pick and position an image first.");
      return;
    }
    setBusy(true);
    try {
      const blob = await getCroppedBlob(imageSrc, pixelCrop, rotation, preset.w, preset.h);
      if (!blob) throw new Error("Crop failed");
      const form = new FormData();
      form.append("file", blob, "cropped.png");
      const { data } = await api.post("/images/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onSave({ url: data.url, alt: alt.trim(), preset: preset.id, w: preset.w, h: preset.h });
      toast.success(`Image attached · ${preset.w}×${preset.h}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Couldn't attach image.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         data-testid="image-crop-modal"
         style={{ background: "rgba(0,0,0,0.75)" }}
         onDragOver={onDragOver}
         onDragLeave={onDragLeave}
         onDrop={onDrop}>
      <div className="elle-surface rounded-sm max-w-4xl w-full max-h-[92vh] overflow-y-auto"
           style={{ border: "1px solid " + (dragActive ? "var(--elle-amber)" : "var(--elle-border)"),
                    boxShadow: dragActive ? "0 0 0 3px rgba(197,160,89,0.25)" : undefined }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 hairline"
             style={{ borderBottom: "1px solid var(--elle-border)" }}>
          <div>
            <div className="overline">Attach image</div>
            <div className="text-sm mt-1" style={{ color: "var(--elle-ink)" }}>
              {platform} · {preset.label} · {preset.w}×{preset.h}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close"
                  data-testid="image-crop-close"
                  className="p-2 rounded-sm hover:bg-white/5">
            <XIcon size={16}/>
          </button>
        </div>

        {/* Spec callout — always visible so Elle can see what the platform
            expects before and during cropping. */}
        <div className="mx-5 mt-4 p-3 rounded-sm text-xs"
             data-testid="platform-spec"
             style={{ background: "rgba(197,160,89,0.06)",
                      border: "1px solid var(--elle-border)",
                      borderLeft: "3px solid var(--elle-amber)",
                      color: "var(--elle-ink-dim)" }}>
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            <span>
              <span className="font-mono-elle" style={{ color: "var(--elle-ink-faint)" }}>Target size:</span>{" "}
              <strong style={{ color: "var(--elle-ink)" }}>{preset.w} × {preset.h} px</strong>
            </span>
            <span>
              <span className="font-mono-elle" style={{ color: "var(--elle-ink-faint)" }}>Typical output:</span>{" "}
              <strong style={{ color: "var(--elle-ink)" }}>{preset.typicalKb} KB</strong>
            </span>
            <span>
              <span className="font-mono-elle" style={{ color: "var(--elle-ink-faint)" }}>{platform} max:</span>{" "}
              <strong style={{ color: "var(--elle-ink)" }}>{formatKb(spec.maxKb)}</strong>
            </span>
          </div>
          <div className="mt-1 font-mono-elle" style={{ color: "var(--elle-ink-faint)" }}>
            {spec.formatNote}
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {!imageSrc ? (
            <label htmlFor="image-upload-input"
                   className="block cursor-pointer rounded-sm p-12 text-center transition-all hover:bg-white/5"
                   data-testid="image-dropzone"
                   style={{ border: "2px dashed " + (dragActive ? "var(--elle-amber)" : "var(--elle-border)"),
                            background: dragActive ? "rgba(197,160,89,0.06)" : undefined }}>
              <Upload size={28} className="mx-auto mb-3"
                      style={{ color: dragActive ? "var(--elle-amber)" : "var(--elle-ink-dim)" }}/>
              <div className="text-sm" style={{ color: "var(--elle-ink)" }}>
                {dragActive
                  ? "Drop to attach"
                  : "Click to choose an image, or drag one here."}
              </div>
              <div className="text-xs font-mono-elle mt-2" style={{ color: "var(--elle-ink-faint)" }}>
                PNG, JPG, or WebP · up to 20 MB source file · crops to {preset.w}×{preset.h} px (~{preset.typicalKb} KB)
              </div>
              <input id="image-upload-input" type="file" accept="image/*"
                     data-testid="image-file-input"
                     onChange={(e) => handleFile(e.target.files?.[0])}
                     className="hidden"/>
            </label>
          ) : (
            <>
              <div className="flex flex-wrap gap-2" data-testid="preset-chips">
                {presets.map((p, i) => (
                  <button key={p.id} type="button"
                          onClick={() => setPresetIdx(i)}
                          data-testid={`preset-${p.id}`}
                          className="text-xs font-mono-elle px-3 py-1.5 rounded-sm transition-colors"
                          style={{
                            border: "1px solid " + (presetIdx === i ? "var(--elle-amber)" : "var(--elle-border)"),
                            color: presetIdx === i ? "var(--elle-amber-bright)" : "var(--elle-ink-dim)",
                            background: presetIdx === i ? "rgba(197,160,89,0.08)" : "transparent",
                          }}>
                    {p.label}
                  </button>
                ))}
              </div>

              <div className="relative w-full rounded-sm overflow-hidden"
                   style={{ height: 420, background: "#000" }}
                   data-testid="cropper-surface">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={preset.aspect}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onRotationChange={setRotation}
                  onCropComplete={onCropComplete}
                  objectFit="contain"
                  restrictPosition={true}
                />
              </div>

              {/* Controls */}
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                  <ZoomOut size={14} style={{ color: "var(--elle-ink-faint)" }}/>
                  <input type="range" min="1" max="4" step="0.01"
                         value={zoom}
                         data-testid="zoom-slider"
                         onChange={(e) => setZoom(parseFloat(e.target.value))}
                         className="flex-1 accent-[var(--elle-amber)]"/>
                  <ZoomIn size={14} style={{ color: "var(--elle-ink-faint)" }}/>
                </div>
                <button type="button"
                        onClick={() => setRotation((r) => (r + 90) % 360)}
                        data-testid="rotate-btn"
                        className="text-xs font-mono-elle px-3 py-1.5 rounded-sm hover:text-[var(--elle-amber)] transition-colors inline-flex items-center gap-1.5"
                        style={{ border: "1px solid var(--elle-border)", color: "var(--elle-ink-dim)" }}>
                  <RotateCw size={12}/> Rotate 90°
                </button>
                <button type="button"
                        onClick={() => { setFile(null); setImageSrc(null); }}
                        data-testid="choose-different-image"
                        className="text-xs font-mono-elle underline"
                        style={{ color: "var(--elle-ink-faint)" }}>
                  choose different image
                </button>
              </div>

              {/* Alt text */}
              <div>
                <label className="overline block mb-1" htmlFor="alt-input">
                  Alt text <span style={{ color: "var(--elle-ink-faint)" }}>(recommended — describe what's in the image)</span>
                </label>
                <Input id="alt-input" type="text"
                       value={alt}
                       onChange={(e) => setAlt(e.target.value)}
                       placeholder="e.g. A 19th-century illustration of an Orc holding a manuscript."
                       data-testid="image-alt-input"
                       className="text-sm"/>
                <p className="text-xs font-mono-elle mt-1"
                   style={{ color: "var(--elle-ink-faint)" }}>
                  Shipped with Bluesky posts for screen-readers and stored for Meta when that adapter ships.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 hairline"
             style={{ borderTop: "1px solid var(--elle-border)" }}>
          <Button variant="outline" onClick={onClose}
                  className="btn-ghost rounded-sm"
                  data-testid="image-crop-cancel">
            Cancel
          </Button>
          <Button onClick={save}
                  disabled={busy || !imageSrc}
                  data-testid="image-crop-save"
                  className="btn-amber rounded-sm">
            <Check size={14} className="mr-2"/>
            {busy ? "Attaching…" : "Attach cropped image"}
          </Button>
        </div>
      </div>
    </div>
  );
}
